import { useEffect, useRef, useState, type ComponentType } from "react";
import { Outlet, useLocation, useNavigate, useRouter } from "@tanstack/react-router";
import type { StaffPermission } from "@shared/domain";
import { PIN_LENGTH, isWeakPin } from "@shared/pin";
import { api, errorMessage } from "@/lib/api";
import { cn } from "@/lib/cn";
import { endpoints } from "@/lib/endpoints";
import { queryClient } from "@/lib/queryClient";
import { queryKeys } from "@/lib/queryKeys";
import { toast } from "@/stores/feedback";
import { Sheet } from "@/components/overlays";
import { Segmented } from "@/components/ui";
import { AltArrowLeftIcon, ClipboardCheckIcon, HomeSmileIcon, KeyMinimalisticIcon, SledgehammerIcon, TShirtIcon } from "@/components/icons";
import { useStaffCan, useStaffHome, useStaffUser } from "./api";
import { AUTO_SUBMIT_MS, PinDots, PinPad } from "./auth/PinPad";
import { usePreviewMode } from "./preview/mode";
import { ActionSlot, useChrome } from "./chrome";
import { Avatar, Badge } from "./ui";

/*
 * The staff app's frame: a top bar naming the screen (no logo, no property),
 * the screen, an action bar on screens that commit something, and bottom
 * tabs for the areas this person may open. Phone first; on a tablet the same
 * single column, centred at 720px.
 */

type Tab = { to: string; label: string; Icon: ComponentType<{ size?: number }>; needs: StaffPermission[]; badge?: "checks" | "repairs" };

const TABS: Tab[] = [
  { to: "/staff", label: "Home", Icon: HomeSmileIcon, needs: [] },
  { to: "/staff/checks", label: "Checks", Icon: ClipboardCheckIcon, needs: ["checks.do", "checks.take"], badge: "checks" },
  { to: "/staff/repairs", label: "Repairs", Icon: SledgehammerIcon, needs: ["repairs.fix", "repairs.report"], badge: "repairs" },
  { to: "/staff/laundry", label: "Laundry", Icon: TShirtIcon, needs: ["laundry.batches", "laundry.stock"] },
  { to: "/staff/keys", label: "Keys", Icon: KeyMinimalisticIcon, needs: ["access.view", "access.rotate"] },
];

const TEXT_SIZE_KEY = "kk.staff.textSize";

function readTextSize(): "normal" | "large" {
  try {
    return localStorage.getItem(TEXT_SIZE_KEY) === "large" ? "large" : "normal";
  } catch {
    return "normal";
  }
}

export function StaffShell() {
  const chrome = useChrome();
  const preview = usePreviewMode();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const scroller = useRef<HTMLDivElement>(null);
  const [slot, setSlot] = useState<HTMLDivElement | null>(null);
  const [account, setAccount] = useState(false);
  const [textSize, setTextSize] = useState(readTextSize);
  const me = useStaffUser();

  // Every screen starts at the top.
  // A block body: newer browsers return a Promise from scrollTo, and an effect must return nothing but a cleanup.
  useEffect(() => {
    scroller.current?.scrollTo({ top: 0 });
  }, [pathname]);

  // The toast sits above the tab bar (or the action bar).
  useEffect(() => {
    document.documentElement.style.setProperty("--toast-lift", "70px");
    const theme = document.querySelector('meta[name="theme-color"]');
    const before = theme?.getAttribute("content") ?? null;
    theme?.setAttribute("content", "#FAF4E8");
    return () => {
      document.documentElement.style.removeProperty("--toast-lift");
      if (before) theme?.setAttribute("content", before);
    };
  }, []);

  return (
    <div className="flex h-dvh flex-col bg-canvas text-ink" style={textSize === "large" ? { zoom: 1.12 } : undefined}>
      {preview && (
        <div className="flex h-7 shrink-0 items-center justify-center bg-brass font-mono text-[11px] font-semibold tracking-[.08em] text-brass-ink uppercase">Preview · nothing here is saved</div>
      )}
      <header className="flex h-[60px] shrink-0 items-center gap-1 border-b border-line bg-surface px-2">
        {chrome.back ? (
          <button type="button" aria-label="Back" onClick={() => void navigate({ to: chrome.back! })} className="flex size-11 shrink-0 items-center justify-center rounded-full active:bg-sunk">
            <AltArrowLeftIcon size={24} />
          </button>
        ) : (
          <span className="w-2" />
        )}
        <div className="min-w-0 flex-1">
          <div className="truncate font-display text-[19px] leading-tight font-semibold">{chrome.title}</div>
          {chrome.sub && <div className="truncate text-[13px] text-muted">{chrome.sub}</div>}
        </div>
        <button type="button" aria-label="Your account" onClick={() => setAccount(true)} className="flex size-11 shrink-0 items-center justify-center rounded-full active:bg-sunk">
          <Avatar initials={me.initials} />
        </button>
      </header>

      <div ref={scroller} className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
        <main className="mx-auto flex max-w-[720px] flex-col gap-3.5 px-4 pt-4 pb-7">
          <ActionSlot.Provider value={slot}>
            <Outlet />
          </ActionSlot.Provider>
        </main>
      </div>

      <div ref={setSlot} className={cn(!chrome.commit && "hidden")} />
      {!chrome.commit && <TabBar pathname={pathname} />}

      <AccountSheet
        open={account}
        onClose={() => setAccount(false)}
        textSize={textSize}
        onTextSize={(v) => {
          setTextSize(v);
          try {
            localStorage.setItem(TEXT_SIZE_KEY, v);
          } catch {
            /* A device preference: losing it costs nothing. */
          }
        }}
      />
    </div>
  );
}

function TabBar({ pathname }: { pathname: string }) {
  const navigate = useNavigate();
  const home = useStaffHome();
  const canChecks = useStaffCan("checks.do", "checks.take");
  const canRepairs = useStaffCan("repairs.fix", "repairs.report");
  const canLaundry = useStaffCan("laundry.batches", "laundry.stock");
  const canKeys = useStaffCan("access.view", "access.rotate");
  const allowed: Record<string, boolean> = { "/staff": true, "/staff/checks": canChecks, "/staff/repairs": canRepairs, "/staff/laundry": canLaundry, "/staff/keys": canKeys };
  const tabs = TABS.filter((t) => allowed[t.to]);
  const badge = (b: Tab["badge"]) => (b === "checks" ? (home.data?.checks?.todo ?? 0) : b === "repairs" ? (home.data?.repairs?.count ?? 0) : 0);
  const active = (to: string) => (to === "/staff" ? pathname === "/staff" || pathname === "/staff/" || pathname.startsWith("/staff/lost") : pathname.startsWith(to));
  return (
    <nav className="flex shrink-0 border-t border-line-strong bg-surface pb-[env(safe-area-inset-bottom)]" aria-label="Sections">
      {tabs.map((t) => {
        const on = active(t.to);
        return (
          <button
            key={t.to}
            type="button"
            aria-current={on ? "page" : undefined}
            onClick={() => void navigate({ to: t.to })}
            className={cn("relative flex h-[62px] flex-1 flex-col items-center justify-center gap-0.5 active:bg-sunk", on ? "font-bold text-deodar" : "text-muted")}
          >
            {on && <span aria-hidden className="absolute inset-x-[22%] top-0 h-[3px] rounded-b-[3px] bg-deodar" />}
            <span className="relative">
              <t.Icon size={24} />
              <Badge n={badge(t.badge)} className="absolute -top-1.5 -right-3" />
            </span>
            <span className="text-[11.5px]">{t.label}</span>
          </button>
        );
      })}
    </nav>
  );
}

function AccountSheet({ open, onClose, textSize, onTextSize }: { open: boolean; onClose: () => void; textSize: "normal" | "large"; onTextSize: (v: "normal" | "large") => void }) {
  const me = useStaffUser();
  const preview = usePreviewMode();
  const navigate = useNavigate();
  const router = useRouter();
  const [changing, setChanging] = useState(false);
  const signOut = async () => {
    await api(endpoints.staffAuth.signOut, { method: "POST", quiet401: true }).catch(() => null);
    queryClient.removeQueries({ queryKey: queryKeys.staff.all });
    // The device cookie stays: next time opens on "Hi {name}".
    queryClient.removeQueries({ queryKey: queryKeys.staff.device });
    onClose();
    await navigate({ to: "/staff/sign-in", search: {}, replace: true });
    void router.invalidate();
  };
  return (
    <>
      <Sheet open={open && !changing} onClose={onClose} label="Your account">
        <div className="flex flex-col gap-5 px-5 pt-1 pb-[calc(20px+env(safe-area-inset-bottom))]">
          <div className="flex items-center gap-3">
            <Avatar initials={me.initials} />
            <div className="font-display text-[20px] font-semibold">{me.name}</div>
          </div>
          <div className="flex items-center justify-between gap-3">
            <span className="text-[15.5px] font-semibold">Text size</span>
            <Segmented
              value={textSize}
              onChange={onTextSize}
              options={[
                { value: "normal", label: "Normal" },
                { value: "large", label: "Large" },
              ]}
            />
          </div>
          <button type="button" disabled={preview} onClick={() => setChanging(true)} className="min-h-[52px] rounded-md border border-line-strong bg-sunk text-[15.5px] font-semibold active:bg-canvas disabled:opacity-[.42]">
            Change PIN
          </button>
          <button type="button" disabled={preview} onClick={() => void signOut()} className="min-h-[52px] rounded-md text-[15.5px] font-semibold text-rhodo active:bg-sunk disabled:opacity-[.42]">
            Sign out
          </button>
        </div>
      </Sheet>
      <ChangePinSheet
        open={open && changing}
        onClose={() => {
          setChanging(false);
          onClose();
        }}
      />
    </>
  );
}

/** Current → new → again. */
function ChangePinSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [step, setStep] = useState<"current" | "next" | "again">("current");
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [pin, setPin] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  useEffect(() => () => clearTimeout(timer.current), []);

  const reset = () => {
    setStep("current");
    setCurrent("");
    setNext("");
    setPin("");
    setError(null);
  };
  const close = () => {
    reset();
    onClose();
  };

  const save = async (again: string) => {
    setBusy(true);
    try {
      await api(endpoints.staff.changePin, { method: "POST", body: { current, next: again }, quiet401: true });
      toast.saved("Your PIN is changed");
      close();
    } catch (e) {
      reset();
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const onPin = (v: string) => {
    setError(null);
    setPin(v);
    clearTimeout(timer.current);
    if (v.length !== PIN_LENGTH) return;
    timer.current = setTimeout(() => {
      setPin("");
      if (step === "current") {
        setCurrent(v);
        setStep("next");
      } else if (step === "next") {
        if (isWeakPin(v)) return setError("Too easy to guess. Try another.");
        if (v === current) return setError("That's the PIN you have now. Pick a new one.");
        setNext(v);
        setStep("again");
      } else if (v !== next) {
        setStep("next");
        setNext("");
        setError("Those didn't match. Start again.");
      } else void save(v);
    }, AUTO_SUBMIT_MS);
  };

  const title = step === "current" ? "Your current PIN" : step === "next" ? "Choose a new PIN" : "Enter it again";
  return (
    <Sheet open={open} onClose={close} label="Change PIN">
      <div className="flex flex-col gap-4 px-5 pt-1 pb-[calc(20px+env(safe-area-inset-bottom))]">
        <h2 className="font-display text-[22px] font-semibold">{title}</h2>
        <PinDots length={pin.length} error={!!error} />
        <p className={cn("min-h-6 text-center text-[15px] font-semibold text-rhodo", !error && "invisible")}>{error ?? "·"}</p>
        <PinPad value={pin} onChange={onPin} disabled={busy} />
      </div>
    </Sheet>
  );
}
